import React, { useState, useEffect } from 'react';

interface SubItem {
  label: string;
  badge?: string;
  targetId: string;
}

interface NavItem {
  id: string;
  label: string;
  scrollTop?: boolean;
  sub?: SubItem[];
}

const navItems: NavItem[] = [
  { id: 'home', label: 'Home', scrollTop: true },
  { id: 'about', label: 'About' },
  {
    id: 'experience',
    label: 'Experience',
    sub: [
      { label: 'Mawari Technologies', badge: 'NOW', targetId: 'experience-mawari' },
      { label: 'Naolito Animation Studio', targetId: 'experience-naolito' },
    ],
  },
  {
    id: 'skills',
    label: 'Skills',
    sub: [
      { label: 'Rendering APIs & Engines', targetId: 'skills-graphics-apis' },
      { label: 'Rendering Architecture', targetId: 'skills-rendering-techniques' },
      { label: 'Compute, Shaders & Profiling', targetId: 'skills-gpu-ml' },
    ],
  },
  {
    id: 'projects',
    label: 'Projects',
    sub: [
      { label: 'CodeCudaEngine', badge: 'CU', targetId: 'project-codecudaengine' },
      { label: 'CodeVkEngine', badge: 'VK', targetId: 'project-codevkengine' },
      { label: 'Vulkan Path Tracer', badge: 'RT', targetId: 'project-vulkan-path-tracer' },
      { label: 'GPU Benchmark Suite', targetId: 'project-gpu-benchmark-suite' },
      { label: 'Code-Game-Engine', badge: 'GL', targetId: 'project-code-game-engine' },
      { label: 'CudaKernels_Viz', badge: 'CU', targetId: 'project-cudakernels-viz' },
      { label: 'MatrixMul_Viz', targetId: 'project-matrixmul-viz' },
      { label: 'Transformer LLM', badge: 'AI', targetId: 'project-transformer-llm' },
    ],
  },
  {
    id: 'education',
    label: 'Education',
    sub: [
      { label: 'Centennial College', badge: 'CA', targetId: 'education-centennial' },
      { label: 'EVAD Málaga', badge: 'ES', targetId: 'education-evad' },
    ],
  },
];

const Sidebar: React.FC = () => {
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const ids = navItems.filter((n) => !n.scrollTop).map((n) => n.id);
    const observers: IntersectionObserver[] = [];

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
        { threshold: 0.15 }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const scrollTo = (id: string, scrollTop?: boolean) => {
    if (scrollTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.getElementById(id);
      if (!element) return;

      const topOffset = 88;
      const rect = element.getBoundingClientRect();
      const maxTop = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
      const targetTop = Math.min(Math.max(window.scrollY + rect.top - topOffset, 0), maxTop);

      window.scrollTo({ top: targetTop, behavior: 'smooth' });
    }
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-toc-header">Table of Contents</div>
      <nav className="sidebar-nav">
        {navItems.map((item) => {
          const isActive = !item.scrollTop && activeSection === item.id;
          const subCount = item.sub?.length || 0;
          return (
            <div key={item.id}>
              <button
                className={`sidebar-item${isActive ? ' active' : ''}`}
                onClick={() => scrollTo(item.id, item.scrollTop)}
              >
                <span>{item.label}</span>
                {subCount > 0 && (
                  <span style={{ fontSize: '11px', opacity: 0.4, transition: 'opacity 0.15s' }}>
                    {isActive ? '−' : '+'}
                  </span>
                )}
              </button>

              {/* Sub-items: only visible when section is active */}
              <div style={{
                maxHeight: isActive ? `${subCount * 30}px` : '0',
                overflow: 'hidden',
                transition: 'max-height 0.25s ease',
              }}>
                {item.sub?.map((sub, i) => (
                  <button
                    key={i}
                    className="sidebar-subitem"
                    onClick={() => scrollTo(sub.targetId)}
                  >
                    <span>{sub.label}</span>
                    {sub.badge && (
                      <span className="sidebar-badge">{sub.badge}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

export default Sidebar;
